import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle, BriefcaseBusiness, Check, Eye, MailCheck, Plus, Search, X, UserCog, Loader2, Send,
  Award, FileSignature, AlertCircle, FolderOpen,
} from 'lucide-react';
import { adminApi } from '../services/api';
import EtatVide from '../components/EtatVide';
import PiedTableau from '../components/PiedTableau';
import {
  annoncerErreur, annoncerSucces, confirmer, EtatErreur, EtatSansDonnees, Squelette, SqueletteTableau,
} from '../etats';

const STATUT_CONFIG = {
  en_attente:         { label: 'En attente',     color: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  contrat_en_attente: { label: 'Contrat envoyé', color: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  contrat_signe:      { label: 'Contrat signé',  color: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  en_cours:           { label: 'En cours',       color: 'text-[var(--accent-texte)] bg-[var(--vert-pale)] border-[var(--border)]' },
  certification:      { label: 'En certification', color: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  termine:            { label: 'Terminé',        color: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]' },
  annule:             { label: 'Annulé',         color: 'text-[var(--alerte)] bg-[var(--alerte-fond)] border-[var(--border)]' },
};

const TYPE_CONFIG = {
  certification:   { label: 'Certification',  Icon: Award,          className: 'bg-[var(--info-fond)] text-[var(--text-bold)] border-[var(--border)]' },
  accompagnement:  { label: 'Accompagnement', Icon: FileSignature,  className: 'bg-[var(--vert-pale)] text-[var(--vert-fonce)] border-[var(--border)]' },
};

function StatutBadge({ statut }) {
  const cfg = STATUT_CONFIG[statut] || { label: statut, color: 'text-[var(--text-muted)] bg-[var(--bg-main)] border-[var(--border)]' };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.accompagnement;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${cfg.className}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function CreateProjetModal({ coaches, onClose, onCreate }) {
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ organisation: '', coach: '', type_projet: 'accompagnement', formule: '' });
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [acces, setAcces] = useState(null); // accès générés après création
  const [formules, setFormules] = useState([]);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    adminApi.getOrganisations()
      .then(data => setOrgs(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoadingOrgs(false));
    // Le projet ouvert hors plateforme n'a pas de formule choisie en ligne :
    // c'est ici que l'administration fixe celle qui a été convenue.
    adminApi.formulesAdmin()
      .then(d => setFormules((d?.formules || []).filter(f => f.actif)))
      .catch(() => {});
  }, []);

  // Une formule n'a de sens que pour la nature de projet qu'elle vend.
  const CATEGORIES = {
    accompagnement: ['accompagnement', 'complet'],
    formation: ['formation'],
    certification: ['labellisation'],
  };
  const formulesPossibles = formules.filter(f => (CATEGORIES[form.type_projet] || []).includes(f.categorie));
  const formuleChoisie = formules.find(f => f.code === form.formule);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.organisation) return;
    setLoading(true);
    setErreur('');
    try {
      const payload = { organisation: form.organisation, type_projet: form.type_projet };
      if (form.coach) payload.coach = form.coach;
      if (form.formule) payload.formule = form.formule;
      const result = await onCreate(payload);
      if (result?.acces_generes) setAcces(result.acces_generes);
      else onClose();
    } catch (e) {
      setErreur(e?.message || 'Création impossible.');
    } finally {
      setLoading(false);
    }
  };


  // ── Écran de confirmation d'accès ────────────────────────────────────────
  // Il affichait l'identifiant et le mot de passe temporaire, avec deux
  // boutons « Copier » et un avertissement « notez-les maintenant ». Plus rien
  // n'est à noter : l'association reçoit un lien, choisit son mot de passe, et
  // personne ici ne le connaîtra jamais.
  if (acces) {
    const cas = {
      cree: {
        titre: 'Accès ouvert',
        texte: `Un lien d'activation vient d'être envoyé à ${acces.email}. L'association y choisira son mot de passe ; ce lien ne sert qu'une fois.`,
        ton: 'ok',
      },
      existant: {
        titre: 'Compte déjà ouvert',
        texte: acces.lien_envoye
          ? `Cette organisation avait déjà un accès, jamais activé. Un nouveau lien vient de partir vers ${acces.email}.`
          : `Cette organisation a déjà un accès actif (${acces.email}). Elle se connecte avec son mot de passe habituel.`,
        ton: 'ok',
      },
      conflit: {
        titre: 'Accès non ouvert',
        texte: acces.erreur,
        ton: 'alerte',
      },
      sans_email: {
        titre: 'Accès non ouvert',
        texte: "Cette organisation n'a pas d'adresse e-mail. Renseignez-la dans sa fiche, puis utilisez « Renvoyer le lien » sur le projet.",
        ton: 'alerte',
      },
    }[acces.statut] || { titre: 'Projet créé', texte: '', ton: 'ok' };

    const alerte = cas.ton === 'alerte';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-pastille flex items-center justify-center ${
              alerte ? 'bg-[var(--attention-fond)]' : 'bg-[var(--accent)]/15'}`}>
              {alerte
                ? <AlertTriangle className="w-5 h-5 text-[var(--attention)]" />
                : <MailCheck className="w-5 h-5 text-[var(--accent-texte)]" />}
            </div>
            <h3 className="text-xl font-semibold text-[var(--text-bold)]">{cas.titre}</h3>
          </div>

          <p className="text-sm text-[var(--text-muted)] leading-relaxed mb-6">{cas.texte}</p>

          {!alerte && acces.email && (
            <div className="rounded-pastille bg-[var(--bg-main)] px-4 py-3 mb-6">
              <p className="sous-titre-page">Identifiant de l'association</p>
              <p className="font-semibold text-[var(--text-bold)] text-sm mt-0.5 break-all">{acces.email}</p>
            </div>
          )}

          <button onClick={onClose} style={{ minHeight: 44 }}
            className="act w-full">
            Fermer
          </button>
        </motion.div>
      </div>
    );
  }

  // ── Formulaire de création ────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">Créer un projet</h3>
          <button onClick={onClose} className="act act-icone act-tertiaire">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Organisation *
            </label>
            {loadingOrgs ? (
              <div className="h-11 rounded-pastille bg-[var(--bg-main)] animate-pulse" />
            ) : (
              <select
                value={form.organisation}
                onChange={set('organisation')}
                className="champ font-semibold"
              >
                <option value="">— Sélectionner une organisation —</option>
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>{o.nom}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Nature du projet
            </label>
            <select value={form.type_projet}
              onChange={e => setForm(p => ({ ...p, type_projet: e.target.value, formule: '' }))}
              className="champ font-semibold">
              <option value="accompagnement">Accompagnement</option>
              <option value="formation">Formation</option>
              <option value="certification">Certification</option>
            </select>
          </div>

          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Formule convenue
            </label>
            <select value={form.formule} onChange={set('formule')} className="champ font-semibold">
              <option value="">— À fixer plus tard —</option>
              {formulesPossibles.map(f => (
                <option key={f.code} value={f.code}>{f.nom}</option>
              ))}
            </select>
            {/* Ce que la plateforme fera respecter : on le voit avant de créer. */}
            {formuleChoisie?.avantages_chiffres?.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {formuleChoisie.avantages_chiffres.map(a => (
                  <li key={a.code} className="px-2 py-0.5 rounded-controle text-[11.5px] font-semibold bg-[var(--accent)]/12 text-[var(--accent-texte)]">
                    {a.phrase}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Coach / mentor (optionnel)
            </label>
            <select
              value={form.coach}
              onChange={set('coach')}
              className="champ font-semibold"
            >
              <option value="">— Affecter plus tard —</option>
              {coaches.map(c => (
                <option key={c.id} value={c.id}>
                  {c.display_name} · {c.profil_display} ({c.email})
                </option>
              ))}
            </select>
          </div>
        </div>

        {erreur && <div className="encart encart-alerte mt-4">{erreur}</div>}

        <div className="flex gap-3 mt-6">
          <button onClick={onClose}
            className="act act-contour flex-1">
            Annuler
          </button>
          <button onClick={submit} disabled={loading || !form.organisation}
            className="act flex-1">
            {loading ? 'Création...' : 'Créer le projet'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function ValiderModal({ projet, coaches, onClose, onValidate }) {
  const [coachId, setCoachId] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await onValidate(projet.id, coachId || null);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
      >
        <h3 className="text-xl font-semibold text-[var(--text-bold)] mb-2">Valider le projet</h3>
        <p className="text-sm text-[var(--text-muted)] mb-6">
          Organisation : <span className="font-bold text-[var(--text-main)]">{projet.organisation_nom}</span>
        </p>

        <label className="block surtitre text-[var(--text-muted)] mb-2">
          Affecter un coach (optionnel)
        </label>
        <select
          value={coachId}
          onChange={e => setCoachId(e.target.value)}
          className="champ font-semibold mb-6"
        >
          <option value="">— Aucun coach pour l'instant —</option>
          {coaches.map(c => (
            <option key={c.id} value={c.id}>{c.display_name} ({c.email})</option>
          ))}
        </select>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="act act-contour flex-1"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="act flex-1"
          >
            {loading ? 'Validation...' : 'Valider & Démarrer'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function Projets() {
  const navigate = useNavigate();
  const [projets, setProjets] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [modalProjet, setModalProjet] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailFeedback, setEmailFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, c] = await Promise.all([
        adminApi.getProjets(filterStatut || undefined),
        adminApi.getCoaches(),
      ]);
      setProjets(Array.isArray(p) ? p : []);
      setCoaches(Array.isArray(c) ? c : []);
      setCharge(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filterStatut]);

  useEffect(() => { load(); }, [load]);

  const handleValidate = async (projetId, coachId) => {
    await adminApi.validerProjet(projetId, coachId);
    annoncerSucces('Projet validé : il démarre et l’organisation est prévenue.');
    load();
  };

  const handleAnnuler = async (projetId) => {
    if (!await confirmer('Annuler ce projet ? L’organisation ne pourra plus y accéder.')) return;
    try {
      await adminApi.annulerProjet(projetId);
      annoncerSucces('Projet annulé.');
      load();
    } catch (e) {
      annoncerErreur(e.message || 'Le projet n’a pas pu être annulé.');
    }
  };

  const handleCreate = async (data) => {
    const result = await adminApi.createProjet(data);
    load();
    return result;
  };

  const handleEnvoyerIdentifiants = async (projetId) => {
    setSendingEmail(projetId);
    setEmailFeedback(null);
    try {
      const result = await adminApi.envoyerIdentifiants(projetId);
      annoncerSucces(result?.message || 'Lien d’activation envoyé à l’association.');
    } catch (e) {
      // Le bouton reste rouge un instant : on voit sur QUELLE ligne l'envoi a échoué.
      setEmailFeedback({ type: 'error', projetId });
      annoncerErreur(e.message || 'Le lien n’a pas pu être envoyé.');
      setTimeout(() => setEmailFeedback(null), 4000);
    } finally {
      setSendingEmail(null);
    }
  };

  const actifs = projets.filter(p => p.statut !== 'annule');
  const nbAnnules = projets.filter(p => p.statut === 'annule').length;
  const nbContratsSigner = projets.filter(p => p.statut === 'contrat_signe').length;

  const filtered = projets.filter(p => {
    if (!filterStatut && p.statut === 'annule') return false;
    const q = search.toLowerCase();
    return (
      p.organisation_nom?.toLowerCase().includes(q) ||
      p.coach_display?.toLowerCase().includes(q)
    );
  });

  const stats = {
    total: actifs.length,
    en_attente: actifs.filter(p => p.statut === 'en_attente').length,
    en_cours: actifs.filter(p => p.statut === 'en_cours').length,
    termine: actifs.filter(p => p.statut === 'termine').length,
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center">
            <BriefcaseBusiness className="w-6 h-6 text-[var(--accent-texte)]" />
          </div>
          <div>
            <h1 className="titre-page">Projets d'accompagnement</h1>
            <p className="text-sm text-[var(--text-muted)]">{loading && !charge ? 'Chargement des projets…' : `${stats.total} projet${stats.total > 1 ? 's' : ''} au total`}</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="act"
        >
          <Plus className="w-4 h-4" />
          Créer un projet
        </button>
      </div>

      {/* ── Les chiffres ─────────────────────────────────────────────
          Une seule forme pour les six : la carte de la charte, en
          version courte. Elles étaient de trois sortes -- carte à
          filet, encart bleu, encart rouge -- alors qu'elles disent
          toutes la même chose, un décompte. La couleur ne sert plus
          qu'à ce qui APPELLE une action : les annulés et ce qui attend
          une validation. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 mb-8">
        {[
          { libelle: 'Total actifs', valeur: stats.total },
          { libelle: 'En attente', valeur: stats.en_attente, ton: 'attention' },
          { libelle: 'En cours', valeur: stats.en_cours, ton: 'ok' },
          { libelle: 'Terminés', valeur: stats.termine },
          ...(nbContratsSigner > 0
            ? [{ libelle: 'À valider', valeur: nbContratsSigner, ton: 'attention' }]
            : []),
          { libelle: 'Annulés', valeur: nbAnnules, ton: nbAnnules > 0 ? 'alerte' : undefined },
        ].map((s) => (
          <div key={s.libelle} className="carte-chiffre">
            <span className="libelle">{s.libelle}</span>
            {/* Pendant le premier chargement, un « 0 » serait faux : on montre la forme. */}
            {loading && !charge
              ? <Squelette className="h-7 w-10 mt-1" />
              : <span className={`valeur ${s.ton ? `valeur-${s.ton}` : ''}`}>{s.valeur}</span>}
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Rechercher organisation, coach..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="champ champ-loupe"
          />
        </div>
        {/* Un filtre de liste, donc la pilule -- et une largeur propre :
            en `.champ` (largeur 100 %) il prenait 829 px et laissait
            62 px a la recherche, qui est pourtant l'element principal. */}
        <select
          value={filterStatut}
          onChange={e => setFilterStatut(e.target.value)}
          className="filtre shrink-0 w-full sm:w-auto sm:min-w-[200px]"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {/* Premier chargement : la forme du tableau. Ensuite, changer de
          filtre ACTUALISE sans effacer ce qui est affiché. */}
      {loading && !charge ? (
        <SqueletteTableau colonnes={6} />
      ) : error && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={error} onReessayer={load} /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          {search ? (
            <EtatSansDonnees cas="sansResultat" titre={`Aucun projet pour « ${search} »`}
                             action={<button type="button" className="act act-sm act-contour" onClick={() => setSearch('')}>Effacer la recherche</button>} />
          ) : filterStatut ? (
            <EtatSansDonnees cas="sansFiltre" titre="Aucun projet avec ce statut"
                             action={<button type="button" className="act act-sm act-contour" onClick={() => setFilterStatut('')}>Voir tous les statuts</button>} />
          ) : (
            <EtatVide icone={BriefcaseBusiness} titre="Aucun projet pour l’instant"
                      aide="Les demandes en ligne arrivent ici ; vous pouvez aussi en créer un."
                      action={<button type="button" className="act act-sm" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" />Créer un projet</button>} />
          )}
        </div>
      ) : (
        <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'} aria-busy={loading || undefined}>
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="tableau-cartes w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  {['Organisation / Type', 'Coach / mentor', 'Statut', 'Date demande', 'Diagnostics', 'Actions'].map(h => (
                    <th key={h} className="">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  const needsContratValidation = p.statut === 'contrat_signe';
                  return (
                  <tr
                    key={p.id}
                    className={`border-b border-[var(--border)] hover:bg-[var(--bg-main)] transition-colors ${i % 2 === 0 ? '' : 'bg-[var(--bg-main)]/30'}`}
                  >
                    <td data-libelle="Organisation / Type" className="px-5 py-4">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-sm text-[var(--text-bold)]">{p.organisation_nom}</p>
                        {needsContratValidation && (
                          <span title="Contrat signé — validation requise">
                            <AlertCircle className="w-4 h-4 text-[var(--text-bold)] shrink-0" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-[var(--text-muted)]">#{p.id}</p>
                        <TypeBadge type={p.type_projet || 'accompagnement'} />
                      </div>
                    </td>
                    <td data-libelle="Coach / mentor" className="px-5 py-4">
                      {p.coach_display ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-controle bg-[var(--accent)]/20 flex items-center justify-center">
                            <UserCog className="w-3.5 h-3.5 text-[var(--accent-texte)]" />
                          </div>
                          <span className="text-sm font-semibold text-[var(--text-main)]">{p.coach_display}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">Non affecté</span>
                      )}
                    </td>
                    <td data-libelle="Statut" className="px-5 py-4"><StatutBadge statut={p.statut} /></td>
                    <td data-libelle="Date demande" className="px-5 py-4 text-sm text-[var(--text-muted)]">
                      {new Date(p.date_demande).toLocaleDateString('fr-FR')}
                    </td>
                    <td data-libelle="Diagnostics" className="px-5 py-4 text-center">
                      <span className="text-sm font-semibold text-[var(--text-bold)]">{p.diagnostics_count}</span>
                    </td>
                    <td data-actions="" className="px-5 py-4">
                      {/* Une forme pour tous : bouton carré cerné, libellé au
                          survol et pour les lecteurs d'écran. Consulter à
                          gauche, décider à droite — en couleur, parce que
                          cela engage. */}
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/projets/${p.id}`)}
                          className="act act-icone act-sm act-contour"
                          title="Ouvrir le projet"
                          aria-label="Ouvrir le projet"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* Les documents en un clic : ouvrir le projet puis
                            chercher l'onglet faisait deux pas de trop. Seul
                            un accompagnement en comporte (`volets.py`). */}
                        {p.type_projet === 'accompagnement' && (
                          <button
                            type="button"
                            onClick={() => navigate(`/projets/${p.id}?onglet=documents`)}
                            className="act act-icone act-sm act-contour"
                            title="Documents déposés"
                            aria-label="Documents déposés"
                          >
                            <FolderOpen className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleEnvoyerIdentifiants(p.id)}
                          disabled={sendingEmail === p.id}
                          className={`act act-icone act-sm ${
                            emailFeedback?.projetId === p.id && emailFeedback.type === 'error'
                              ? 'act-danger' : 'act-contour'}`}
                          title="Renvoyer le lien d'activation à l'association"
                          aria-label="Renvoyer le lien d'activation à l'association"
                        >
                          {sendingEmail === p.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Send className="w-4 h-4" />
                          }
                        </button>
                        {p.statut === 'en_attente' && (
                          <>
                            <span className="w-px h-5 bg-[var(--border)] mx-1" aria-hidden="true" />
                            <button
                              type="button"
                              onClick={() => setModalProjet(p)}
                              className="act act-icone act-sm act-succes"
                              title="Valider la demande"
                              aria-label="Valider la demande"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAnnuler(p.id)}
                              className="act act-icone act-sm act-danger"
                              title="Annuler la demande"
                              aria-label="Annuler la demande"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PiedTableau affiches={filtered.length} total={projets.length}
                       nom="projets" />
        </div>
        </div>
      )}

      {modalProjet && (
        <ValiderModal
          projet={modalProjet}
          coaches={coaches}
          onClose={() => setModalProjet(null)}
          onValidate={handleValidate}
        />
      )}

      {showCreate && (
        <CreateProjetModal
          coaches={coaches}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </motion.div>
  );
}
